import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Column, Page, Row, Spacing, Typography, useOverlayContext } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { routes } from '~/constants';
import { useDisconnectStanfordTax } from '~/hooks/reactQuery/mutations/useDisconnectStanfordTax';
import { useHistoryLength } from '~/hooks/useNavigationBlocker';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { DisconnectStanfordTaxConfirmation } from './DisconnectStanfordTaxConfirmation/DisconnectStanfordTaxConfirmation';
import { StanfordTaxMapServicesModal } from './StanfordTaxMapServicesModal/StanfordTaxMapServicesModal';
export const GetStanfordTaxSetupPageTID = (parentTestId)=>getTestIds(parentTestId);
export const StanfordTaxSetupPage = ()=>{
    const { t: stanfordTaxTexts } = useLocaleKeys('integrations.setup.stanfordTax');
    const { t: commonTexts } = useLocaleKeys('common');
    const { StanfordTaxSetupPageTID } = useMemo(()=>GetStanfordTaxSetupPageTID(), []);
    const navigate = useNavigate();
    const historyLength = useHistoryLength();
    const { showModal } = useOverlayContext();
    const { disconnectStanfordTax, isLoading } = useDisconnectStanfordTax({
        failureToastTestId: StanfordTaxSetupPageTID.FailureToast()
    });
    const goBack = ()=>{
        if (historyLength > 1) {
            navigate(-1);
        } else {
            navigate(routes.availableApps);
        }
    };
    const renderConfirmation = (param)=>{
        let { dismissModal } = param;
        return /*#__PURE__*/ _jsx(DisconnectStanfordTaxConfirmation, {
            testId: StanfordTaxSetupPageTID.ConfirmDisconnect(),
            dismissModal: dismissModal
        });
    };
    const renderMapServices = (param)=>{
        let { dismissModal } = param;
        return /*#__PURE__*/ _jsx(StanfordTaxMapServicesModal, {
            testId: StanfordTaxSetupPageTID.MapServicesModal(),
            dismissModal: dismissModal
        });
    };
    const openMapServices = ()=>showModal({
            biName: 'stanford_tax_map_services_modal',
            closeOnClickOutside: false,
            closeOnEscapeKey: false,
            content: renderMapServices
        });
    const disconnect = async ()=>{
        const confirmed = await showModal({
            biName: 'disconnect_stanford_tax_modal',
            content: renderConfirmation
        });
        if (confirmed) {
            disconnectStanfordTax(undefined, {
                onSuccess: goBack
            });
        }
    };
    return /*#__PURE__*/ _jsx(Page, {
        testId: StanfordTaxSetupPageTID.Page(),
        header: stanfordTaxTexts.setupTitle(),
        subheader: stanfordTaxTexts.setupSubTitle(),
        onClickBack: goBack,
        children: /*#__PURE__*/ _jsxs(Column, {
            gap: 16,
            alignItems: "stretch",
            children: [
                /*#__PURE__*/ _jsx(Card, {
                    bi: {
                        name: 'stanford_tax_map_services_row'
                    },
                    testId: StanfordTaxSetupPageTID.MapServicesRow(),
                    autoHeight: true,
                    padding: "m",
                    children: /*#__PURE__*/ _jsxs(Row, {
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: Spacing.Space16,
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                children: stanfordTaxTexts.mapServicesDescription()
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                bi: {
                                    name: 'map_services'
                                },
                                testId: StanfordTaxSetupPageTID.MapServices(),
                                onClick: openMapServices,
                                children: stanfordTaxTexts.mapServicesAction()
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(Card, {
                    bi: {
                        name: 'stanford_tax_setup_row'
                    },
                    testId: StanfordTaxSetupPageTID.DisconnectRow(),
                    autoHeight: true,
                    padding: "m",
                    children: /*#__PURE__*/ _jsxs(Row, {
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 16,
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                skin: "error",
                                children: stanfordTaxTexts.disconnectDescription()
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                bi: {
                                    name: 'disconnect_stanford_tax'
                                },
                                intent: Intent.StanfordTaxManageConnection,
                                testId: StanfordTaxSetupPageTID.Disconnect(),
                                skin: "danger",
                                loading: isLoading,
                                onClick: disconnect,
                                children: commonTexts.buttons.disconnect()
                            })
                        ]
                    })
                })
            ]
        })
    });
};
